import { Box, IconButton, Menu, MenuItem, ListItemIcon, ListItemText, alpha, Divider, Typography, keyframes } from '@mui/material';
import { styled } from '@mui/material/styles';
// import HeaderSearch from './Search';
import HeaderNotifications from './Notifications';
import RedPanikAlert from './RedPanikAlert';
import { useAppSelector, useAppDispatch } from 'src/store';
import { helper } from 'src/utils/helper';
import { Chip } from 'src/components';
import Tooltip from 'src/components/Tooltip';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import NotificationImportantIcon from '@mui/icons-material/NotificationImportant';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { translate } from 'src/i18n/tHelper';
import React, { useState } from 'react';
import DepoTanimSlice from 'src/store/Slices/StokEczane/DepoTanimSlice';
import { HastaUyariSlice } from 'src/store/Slices/HastaUyari/HastaUyariSlice';
import { HastaKaraListeSlice } from 'src/store/Slices/HastaUyariListesi/HastaKaraListeSlice';
import SnackbarUtils from 'src/utils/SnackbarUtils';
import { env } from 'src/env';
import { useKullaniciPanikRedOkumaQuery } from 'src/services/RedPanik/RedPanik';

const ModernHeaderButton = styled(IconButton)(
  ({ theme }) => `
    width: 28px;
    height: 28px;
    border-radius: 4px;
    background: transparent;
    border: 1px solid ${alpha(theme.palette.divider, 0.1)};
    transition: all 0.2s ease;
    margin: 0 1px;
    
    &:hover {
      background: ${alpha(theme.palette.primary.main, 0.08)};
      border-color: ${alpha(theme.palette.primary.main, 0.2)};
      transform: translateY(-1px);
      box-shadow: 0 1px 4px ${alpha(theme.palette.primary.main, 0.15)};
    }
    
    &:active {
      transform: translateY(0);
    }
    
    .MuiSvgIcon-root {
      font-size: 14px;
      transition: all 0.2s ease;
    }
    
    &:hover .MuiSvgIcon-root {
      color: ${theme.palette.primary.main};
    }
    
    &.warning-button {
      &:hover {
        background: ${alpha(theme.palette.warning.main, 0.08)};
        border-color: ${alpha(theme.palette.warning.main, 0.2)};
      }
      
      &:hover .MuiSvgIcon-root {
        color: ${theme.palette.warning.main};
      }
    }
`
);

const ModernMenu = styled(Menu)(
  ({ theme }) => `
    .MuiPaper-root {
      border-radius: 12px;
      min-width: 200px;
      box-shadow: 0 8px 32px ${alpha(theme.palette.common.black, 0.12)};
      border: 1px solid ${alpha(theme.palette.divider, 0.08)};
      backdrop-filter: blur(8px);
      background: ${alpha(theme.palette.background.paper, 0.95)};
      padding: 4px;
      margin-top: 4px;
    }
    
    .MuiList-root {
      padding: 0;
    }
`
);

const ModernMenuItem = styled(MenuItem)(
  ({ theme }) => `
    border-radius: 8px;
    margin: 2px 0;
    padding: 8px 12px;
    min-height: 36px;
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
    
    &:hover {
      background: ${alpha(theme.palette.primary.main, 0.08)};
      transform: translateX(2px);
    }
    
    &:active {
      transform: translateX(1px);
    }
    
    .MuiListItemIcon-root {
      min-width: 32px;
      color: ${theme.palette.text.secondary};
      transition: color 0.2s ease;
    }
    
    &:hover .MuiListItemIcon-root {
      color: ${theme.palette.primary.main};
    }
    
    .MuiListItemText-root {
      margin: 0;
      
      .MuiListItemText-primary {
        font-size: 13px;
        font-weight: 500;
        color: ${theme.palette.text.primary};
        transition: color 0.2s ease;
      }
    }
    
    &:hover .MuiListItemText-primary {
      color: ${theme.palette.primary.main};
    }
`
);

const MenuHeader = styled(Box)(
  ({ theme }) => `
    padding: 8px 12px 4px;
    border-bottom: 1px solid ${alpha(theme.palette.divider, 0.08)};
    margin-bottom: 4px;
`
);

const translatePath = "common";
const HeaderButtons = (): JSX.Element => {
  const userInfo = useAppSelector(s => s.userLogin.userInfo);
  const organizasyon = useAppSelector(s => s.userOrganizasyon.organizasyon);
  const kimlikState = useAppSelector(s => s.kimlik.activeRow);
  const muracaatState = useAppSelector(s => s.muracaat.activeRow);
  const selectedDepo = useAppSelector(p => p.DepoTanim.selectedDepo);
  const verikontrol = useAppSelector(s => s.verikontrol?.data);
  const instutionNameGoster = verikontrol?.find((f) => f.id === 1018)?.deger === "E";
  const dispatch = useAppDispatch();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleHastaUyariEkraniOnClick = () => {
    if (kimlikState.id > 0 || muracaatState.id > 0) {
      handleMenuClose();
      dispatch(HastaUyariSlice.actions.setOpen(true));
    } else {
      SnackbarUtils.error(translate(translatePath, "Lütfen hasta kayıt ekranında bir hasta seçiniz."));
    }
  };

  const handleHastaUyariListesiOnClick = () => {
    handleMenuClose();
    dispatch(HastaKaraListeSlice.actions.setOpen(true));
  };

  const handleDepoSecimClick = () => {
    dispatch(DepoTanimSlice.actions.setIsDepoSecimDialogOpen(true));
  };

  const alertQuery = useKullaniciPanikRedOkumaQuery({ organizasyonId: organizasyon.fkOrganizasyon, userId: userInfo.id }, { pollingInterval: 60000 });
  return (
    <Box sx={{ mr: 0 }}>
      <Box
        sx={{
          mx: 0,
          display: 'flex',
          alignItems: 'center',
          gap: '1px'
        }}
        component="span"
      >
        {alertQuery?.data > 0 && (
          <RedPanikAlert count={alertQuery.data} />
        )}
        {instutionNameGoster &&
          <Box
            sx={{
              height: '28px',
              padding: '0 8px',
              borderRadius: '4px',
              background: 'transparent',
              border: '1px solid',
              borderColor: (theme) => alpha(theme.palette.primary.main, 0.2),
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              fontSize: '11px',
              fontWeight: 500,
              color: (theme) => theme.palette.primary.main,
              transition: 'all 0.2s ease',
              margin: '0 1px',
              minWidth: 'auto',
              cursor: 'pointer',
              '&:hover': {
                background: (theme) => alpha(theme.palette.primary.main, 0.08),
                borderColor: (theme) => alpha(theme.palette.primary.main, 0.3),
                boxShadow: (theme) => `0 1px 4px ${alpha(theme.palette.primary.main, 0.15)}`,
              }
            }}>
            {env.REACT_APP_INSTITUTION_NAME}
          </Box>
        }
        {!helper.stringIsNullOrEmpty(selectedDepo.depoAdi) &&
          <Tooltip arrow title={translate(translatePath, "Depo Değiştir")}>
            <Box
              onClick={handleDepoSecimClick}
              sx={{
                height: '28px',
                padding: '0 8px',
                borderRadius: '4px',
                background: 'transparent',
                border: '1px solid',
                borderColor: (theme) => alpha(theme.palette.primary.main, 0.2),
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                fontSize: '11px',
                fontWeight: 500,
                color: (theme) => theme.palette.primary.main,
                transition: 'all 0.2s ease',
                margin: '0 1px',
                minWidth: 'auto',
                cursor: 'pointer',
                '&:hover': {
                  background: (theme) => alpha(theme.palette.primary.main, 0.08),
                  borderColor: (theme) => alpha(theme.palette.primary.main, 0.3),
                  transform: 'translateY(-1px)',
                  boxShadow: (theme) => `0 1px 4px ${alpha(theme.palette.primary.main, 0.15)}`,
                }
              }}
            >
              <LocationOnIcon sx={{ fontSize: '12px' }} />
              {selectedDepo.depoAdi}
            </Box>
          </Tooltip>
        }

        <Tooltip arrow title={translate(translatePath, "Hasta İşlemleri")}>
          <ModernHeaderButton
            className="warning-button"
            onClick={handleMenuClick}
            sx={{
              position: 'relative',
              backgroundColor: open ? 'rgba(255, 193, 7, 0.1)' : undefined,
              width: '28px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <WarningAmberIcon
              sx={{
                color: 'warning.main',
                fontSize: '14px',
              }}
            />
            <KeyboardArrowDownIcon
              sx={{
                color: 'warning.main',
                fontSize: '8px',
                position: 'absolute',
                top: '-2px',
                right: '-2px',
                transition: 'transform 0.2s ease-in-out',
                transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
              }}
            />
          </ModernHeaderButton>
        </Tooltip>
        <ModernMenu
          anchorEl={anchorEl}
          open={open}
          onClose={handleMenuClose}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          slotProps={{
            paper: {
              elevation: 0,
            }
          }}
        >
          <MenuHeader>
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                fontSize: '10px'
              }}
            >
              {translate(translatePath, "Hasta İşlemleri")}
            </Typography>
          </MenuHeader>

          <ModernMenuItem onClick={handleHastaUyariEkraniOnClick}>
            <ListItemIcon>
              <NotificationImportantIcon sx={{ fontSize: '16px' }} />
            </ListItemIcon>
            <ListItemText primary={translate(translatePath, "Hasta Uyarı")} />
          </ModernMenuItem>

          <ModernMenuItem onClick={handleHastaUyariListesiOnClick}>
            <ListItemIcon>
              <FormatListBulletedIcon sx={{ fontSize: '16px' }} />
            </ListItemIcon>
            <ListItemText primary={translate(translatePath, "Hasta Uyarı Listesi")} />
          </ModernMenuItem>
        </ModernMenu>

        <HeaderNotifications key={translate(translatePath, "Bildirimler")} />
      </Box>
    </Box>
  );
}

export default HeaderButtons;
